import React from 'react';

import { Formik, FormikActions, FormikValues } from 'formik';

import SaveIcon from '@mui/icons-material/Save';

import TranslationHelper from '../../../../helpers/TranslationHelper';

import { useToolkitDispatch } from '../../../../hooks';
import useAsyncThunkWithSnackbar from '../../../../hooks/useAsyncThunkWithSnackbar';

import {
    useSelectedTicketAvailableStatuses,
    useTicket,
} from '../../state/index.hooks';
import { changeTicketPaneMode, updateTicket } from '../../state';
import { ticketSchema } from '../../schema';

import PaneHeader from '../../../../components/PaneHeader';
import IconButtonWithTooltip from '../../../../components/IconButtonWithTooltip';
import TicketForm from './TicketForm';

interface IOwnProps {
    refreshGrid: () => void;
}

const TicketEditForm = ({ refreshGrid }: IOwnProps) => {
    const toolkitDispatch = useToolkitDispatch();

    const selectedTicket = useTicket();
    const statuses = useSelectedTicketAvailableStatuses();

    const handleAsyncRequest = useAsyncThunkWithSnackbar();

    if (!selectedTicket) {
        return <></>;
    }
    const initialValues: FormikValues = {
        name: selectedTicket.name,
        status:
            statuses?.filter(
                (status) => status.name === selectedTicket.status
            )?.[0]?.name || '',
        assignedUser: selectedTicket.assignedUser,
        description: selectedTicket.description ?? '',
        notice: selectedTicket.notice?.content || '',
    };
    const handleValues = (
        values: FormikValues,
        { setSubmitting }: FormikActions<FormikValues>
    ) => {
        if (!selectedTicket.id) {
            return;
        }

        const ticket = {
            notice: values.notice,
            status: values.status,
            name: values.name,
            description: values.description,
            assignedUser: values.assignedUser || undefined,
            domainData: selectedTicket.domainData,
            attributes: selectedTicket.attributes,
        };

        handleAsyncRequest({
            asyncAction: updateTicket({ id: selectedTicket.id, ticket }),
            onSuccess: {
                callback: () => {
                    toolkitDispatch(changeTicketPaneMode('preview'));
                    refreshGrid();
                    setSubmitting(false);
                },
                message: TranslationHelper.translate('Ticket updated'),
            },
            onError: {
                callback: () => setSubmitting(false),
            },
        });
    };

    const getRightCustomControls = (
        submitForm: () => void,
        submitting: boolean
    ) => (
        <IconButtonWithTooltip
            title={TranslationHelper.translate('Save')}
            onClick={submitForm}
            disabled={submitting}
        >
            <SaveIcon />
        </IconButtonWithTooltip>
    );
    return (
        <div>
            <Formik
                enableReinitialize={true}
                initialValues={initialValues}
                onSubmit={handleValues}
                validateOnBlur={false}
                validateOnChange={false}
                validationSchema={ticketSchema}
            >
                {({ errors, setFieldValue, submitForm, isSubmitting }) => (
                    <>
                        <PaneHeader
                            title={TranslationHelper.translate('Edit ticket')}
                            onCloseClick={() =>
                                toolkitDispatch(changeTicketPaneMode('preview'))
                            }
                            renderRightCustomControls={() =>
                                getRightCustomControls(submitForm, isSubmitting)
                            }
                        />

                        <TicketForm
                            errors={errors}
                            setFieldValue={setFieldValue}
                        />
                    </>
                )}
            </Formik>
        </div>
    );
};

export default TicketEditForm;
